
import { SimulationConfig, Vector2D, Ball } from './types';

export class CameraService {
  private currentPos: Vector2D = { x: 0, y: 0 };
  private targetPos: Vector2D = { x: 0, y: 0 };
  private initialized: boolean = false;

  public reset(centerX: number, centerY: number) {
    this.currentPos = { x: centerX, y: centerY };
    this.targetPos = { x: centerX, y: centerY };
    this.initialized = true;
  }

  public update(
    canvasWidth: number,
    canvasHeight: number,
    balls: Ball[],
    config: SimulationConfig,
    shakeAmount: number
  ): { x: number, y: number, scale: number } {
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;

    if (!config.cameraFollow || balls.length === 0) {
      // If disabled or no targets, drift back to center smoothly
      if (!config.cameraFollow) {
          this.targetPos.x = centerX;
          this.targetPos.y = centerY;
      }
    } else {
      // Track the first ball
      const targetBall = balls[0];
      
      // 1. Calculate ideal position based on ball position
      let idealX = targetBall.pos.x;
      let idealY = targetBall.pos.y;

      // 2. Add Lookahead based on velocity
      if (config.cameraLookahead > 0) {
        idealX += targetBall.vel.x * config.cameraLookahead * 5;
        idealY += targetBall.vel.y * config.cameraLookahead * 5;
      }

      // 3. Apply Deadzone
      // If the ideal target is close to the current camera position, don't move the target
      // This creates a "loose" feel where the ball can move freely in the center
      const dx = idealX - this.currentPos.x;
      const dy = idealY - this.currentPos.y;
      const dist = Math.sqrt(dx*dx + dy*dy);
      
      if (dist > config.cameraDeadzone) {
         // Pull the target towards the ideal point, but keep it at the edge of the deadzone relative to current pos
         // Actually, simpler logic: The "Target" for the interpolation is the ball. 
         // If ball is inside deadzone relative to camera center, set target TO camera center (so it doesn't move)
         // Otherwise set target to ball
         
         // Let's reverse: Logic is Camera follows Target.
         // Target is Ball.
         // If Ball is within Deadzone of Camera, Camera stays put.
         // If Ball exceeds Deadzone, Camera moves to keep Ball at edge of Deadzone.
         
         const angle = Math.atan2(dy, dx);
         const moveDist = dist - config.cameraDeadzone;
         this.targetPos.x = this.currentPos.x + Math.cos(angle) * moveDist;
         this.targetPos.y = this.currentPos.y + Math.sin(angle) * moveDist;
      }
      // If inside deadzone, targetPos remains currentPos (no movement)
    }

    if (!this.initialized) {
        this.currentPos = { ...this.targetPos };
        this.initialized = true;
    }

    // 4. Smooth Interpolation (Lerp)
    // Lower smoothness value = faster follow? No, usually 'alpha' in lerp. 
    // Let's use config.cameraSmoothness as a "lag" factor. 
    // 1.0 = instant, 0.01 = very slow.
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    
    // Clamp smoothness to avoid NaN or infinite
    const alpha = Math.max(0.001, Math.min(1.0, config.cameraSmoothness));

    this.currentPos.x = lerp(this.currentPos.x, this.targetPos.x, alpha);
    this.currentPos.y = lerp(this.currentPos.y, this.targetPos.y, alpha);

    // Calculate final draw offset
    // We want currentPos to be at the center of the screen.
    // So we translate the world by (Center - CurrentPos)
    let offsetX = centerX - this.currentPos.x;
    let offsetY = centerY - this.currentPos.y;

    // Apply Shake
    if (shakeAmount > 0) {
        offsetX += (Math.random() - 0.5) * shakeAmount;
        offsetY += (Math.random() - 0.5) * shakeAmount;
    }

    return {
        x: offsetX,
        y: offsetY,
        scale: config.cameraZoom || 1.0
    };
  }
}

export const CAMERA = new CameraService();
